import type { ScheduleDetail } from "@/api/schedule/models";
import { UserRoleEnum } from "@/api/user/models";
import { SCHEDULE_STAGE_MANAGEMENT_ALLOWED_ROLES, SCHEDULE_STAGE_REQUIRED_ROLE } from "@/constants/schedule";

/**
 * Verifica se o usuário tem permissão para gerenciar a etapa atual do agendamento.
 * @param role - O papel do usuário.
 * @param schedule - O agendamento.
 * @returns true se o usuário pode gerenciar a etapa, false caso contrário.
 */
export function canManageScheduleStage(role: UserRoleEnum | null, schedule?: ScheduleDetail): boolean {
  if (
    !role ||
    !schedule?.isPresent ||
    schedule.currentStage === "SCHEDULED" ||
    ["CANCELED", "COMPLETED"].includes(schedule.status)
  ) {
    return false;
  }

  return (
    SCHEDULE_STAGE_MANAGEMENT_ALLOWED_ROLES.includes(role) ||
    SCHEDULE_STAGE_REQUIRED_ROLE[schedule.currentStage] === role
  );
}

/** Verifica se o usuário pode editar trocas de vestuário na recepção. */
export function canEditScheduleArtisticProduction(role: UserRoleEnum | null, schedule?: ScheduleDetail): boolean {
  return Boolean(
    role &&
      schedule?.currentStage === "RECEPTION" &&
      schedule.status === "IN_PROGRESS" &&
      [UserRoleEnum.ADMINISTRATOR, UserRoleEnum.COORDINATOR].includes(role),
  );
}

/** Verifica se o usuário pode concluir o atendimento na recepção. */
export function canCompleteSchedule(role: UserRoleEnum | null, schedule?: ScheduleDetail): boolean {
  return Boolean(
    role &&
      schedule?.isPresent &&
      schedule.currentStage === "RECEPTION" &&
      ["IN_PROGRESS", "PAUSED"].includes(schedule.status) &&
      SCHEDULE_STAGE_MANAGEMENT_ALLOWED_ROLES.includes(role),
  );
}

/** Verifica se o usuário pode reabrir um atendimento concluído. */
export function canResumeSchedule(role: UserRoleEnum | null, schedule?: ScheduleDetail): boolean {
  return Boolean(role && schedule?.status === "COMPLETED" && SCHEDULE_STAGE_MANAGEMENT_ALLOWED_ROLES.includes(role));
}

/** Verifica se o usuário pode cancelar o atendimento na recepção. */
export function canCancelSchedule(role: UserRoleEnum | null, schedule?: ScheduleDetail): boolean {
  return Boolean(
    role &&
      schedule?.isPresent &&
      schedule.currentStage === "RECEPTION" &&
      ["IN_PROGRESS", "PAUSED"].includes(schedule.status) &&
      SCHEDULE_STAGE_MANAGEMENT_ALLOWED_ROLES.includes(role),
  );
}

/** Verifica se o usuário pode iniciar o atendimento agendado. */
export function canStartSchedule(
  role: UserRoleEnum | null,
  currentUserId: string | undefined,
  schedule?: ScheduleDetail,
): boolean {
  if (!role || !schedule?.isPresent || schedule.status !== "AWAITING" || schedule.currentStage === "SCHEDULED") {
    return false;
  }

  if (schedule.currentStage === "PRODUCTION") {
    return role === "PRODUCER" && schedule.responsibleProducerId === currentUserId;
  }

  return SCHEDULE_STAGE_REQUIRED_ROLE[schedule.currentStage] === role;
}

/** Verifica se o usuário pode pausar ou retomar o atendimento na etapa atual. */
export function canManageSchedulePause(
  role: UserRoleEnum | null,
  currentUserId: string | undefined,
  schedule: ScheduleDetail | undefined,
  expectedStatus: "IN_PROGRESS" | "PAUSED",
): boolean {
  if (!role || schedule?.status !== expectedStatus) {
    return false;
  }

  if (schedule.currentStage === "PRODUCTION") {
    return role === "PRODUCER" && schedule.responsibleProducerId === currentUserId;
  }

  if (schedule.currentStage === "RECEPTION") {
    return SCHEDULE_STAGE_MANAGEMENT_ALLOWED_ROLES.includes(role);
  }

  return SCHEDULE_STAGE_REQUIRED_ROLE[schedule.currentStage] === role;
}
